import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ChevronLeft } from 'lucide-react'
import { adminApi } from '@/utils/api'
import type { OrganizationUser, OrganizationDetail } from '@/utils/api'

type CoupleRow = {
  id: string
  partnerAName?: string
  partnerBName?: string
  status?: string
  progressPercentage?: number
}

const statusBadge = (status?: string | null) => {
  const s = (status || '').toLowerCase()
  if (s === 'active') return 'bg-[#d1f4e0] text-[#15803d]'
  if (s === 'suspended') return 'bg-red-100 text-red-700'
  if (s === 'pending') return 'bg-[#fef3c7] text-[#d97706]'
  return 'bg-[#ffedd5] text-[#ea580c]'
}

const roleBadge = (role?: string | null) => {
  const r = (role || '').toLowerCase()
  if (r === 'organization_admin' || r === 'org_admin') return 'bg-purple-100 text-purple-700'
  if (r === 'counselor') return 'bg-blue-100 text-blue-700'
  if (r === 'super_admin') return 'bg-red-100 text-red-700'
  return 'bg-[rgba(237,239,243,0.5)] text-[#475467]'
}

export default function AdminOrganizationDetail() {
  const { organizationId } = useParams<{ organizationId: string }>()
  const navigate = useNavigate()
  const [org, setOrg] = useState<OrganizationDetail | null>(null)
  const [members, setMembers] = useState<OrganizationUser[]>([])
  const [couples, setCouples] = useState<CoupleRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!organizationId) {
      setError('Missing organization ID')
      setLoading(false)
      return
    }

    const fetch = async () => {
      try {
        setLoading(true)
        setError(null)

        const [orgRes, membersRes, couplesRes] = await Promise.allSettled([
          adminApi.organizationDetail(organizationId),
          adminApi.organizationMembers(organizationId),
          adminApi.couplesList({ page: 1, limit: 100 }),
        ])

        if (orgRes.status === 'fulfilled') {
          const orgData =
            (orgRes.value as { data?: OrganizationDetail }).data ||
            (orgRes.value as unknown as OrganizationDetail)
          setOrg(orgData)

          // If org response includes users inline, use those as fallback for members
          if (orgData?.users && Array.isArray(orgData.users)) {
            setMembers(orgData.users)
          }
        }

        if (membersRes.status === 'fulfilled') {
          const membersData =
            (membersRes.value as { data?: OrganizationUser[] }).data ||
            (membersRes.value as { data?: { data?: OrganizationUser[] } }).data?.data ||
            (membersRes.value as unknown as OrganizationUser[])
          if (Array.isArray(membersData) && membersData.length > 0) {
            setMembers(membersData)
          }
        }

        if (couplesRes.status === 'fulfilled') {
          const allCouples =
            (couplesRes.value as { data?: { data?: CoupleRow[] } }).data?.data ||
            (couplesRes.value as { data?: CoupleRow[] }).data ||
            []
          // The couples list endpoint may not filter by org, so we show all for now
          // The backend should ideally support filtering by organizationId
          setCouples(Array.isArray(allCouples) ? allCouples : [])
        }
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load organization details')
      } finally {
        setLoading(false)
      }
    }
    void fetch()
  }, [organizationId])

  if (loading) {
    return (
      <div className='w-full min-h-screen bg-[#edeff3] flex items-center justify-center'>
        <p className='text-[#4e4d4d]'>Loading organization details...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className='w-full min-h-screen bg-[#edeff3] p-4 md:p-6 lg:p-8'>
        <button onClick={() => navigate('/admin/organizations')} className='flex items-center gap-2 text-[14px] text-[#4e4d4d] hover:text-[#151515] mb-4'>
          <ChevronLeft className='h-4 w-4' /> Back to Organizations
        </button>
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 text-center'>
          <p className='text-red-600'>{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className='w-full min-h-screen bg-[#edeff3] p-4 md:p-6 lg:p-8'>
      <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 space-y-6'>
        {/* Header */}
        <div className='flex items-center gap-4'>
          <button
            onClick={() => navigate('/admin/organizations')}
            className='w-[32px] h-[32px] bg-[#eaeaea] rounded-full flex items-center justify-center hover:bg-[#d9d9d9]'
          >
            <ChevronLeft className='h-5 w-5' />
          </button>
          <div>
            <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>{org?.name || 'Organization'}</h1>
            <p className='text-[13px] sm:text-[14px] text-[#4e4d4d]'>ID: {organizationId}</p>
          </div>
          <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium capitalize ${statusBadge(org?.status)}`}>
            {org?.status || 'Unknown'}
          </span>
        </div>

        {/* Overview Stats */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
            <p className='text-[13px] text-[#4e4d4d] mb-1'>Internal Members</p>
            <p className='text-[24px] font-bold text-[#151515]'>{members.length}</p>
            <p className='text-[13px] text-[#4e4d4d] mt-1'>Org admins, counselors</p>
          </div>
          <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
            <p className='text-[13px] text-[#4e4d4d] mb-1'>Couples</p>
            <p className='text-[24px] font-bold text-[#151515]'>{couples.length}</p>
            <p className='text-[13px] text-[#4e4d4d] mt-1'>All couples in system</p>
          </div>
          <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
            <p className='text-[13px] text-[#4e4d4d] mb-1'>Status</p>
            <p className='text-lg font-semibold text-[#151515] capitalize'>{org?.status || '-'}</p>
          </div>
        </div>

        {/* Internal Members */}
        <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4'>
          <h2 className='text-[18px] font-bold text-[#151515] mb-4'>Internal Users (Members)</h2>
          {members.length === 0 ? (
            <p className='text-[14px] text-[#4e4d4d]'>No members found for this organization</p>
          ) : (
            <div className='bg-[rgba(237,239,243,0.5)] rounded-[8px] overflow-hidden'>
              <div className='grid grid-cols-6 gap-2 px-4 py-2 font-medium text-[14px] text-[#475467]'>
                <span>Name</span>
                <span>Email</span>
                <span>Phone</span>
                <span>Role</span>
                <span>Status</span>
                <span className='text-right'>Actions</span>
              </div>
              {members.map((m) => {
                const name = [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email || '-'
                return (
                  <div
                    key={m.id}
                    className='grid grid-cols-6 gap-2 px-4 py-3 text-[14px] text-[#101828] border-b border-[#eaecf0] hover:bg-[#f9fafb] bg-white items-center'
                  >
                    <span className='font-semibold'>{name}</span>
                    <span className='text-xs text-[#475467] truncate'>{m.email || '-'}</span>
                    <span className='text-xs text-[#475467]'>{m.phoneNumber || '-'}</span>
                    <span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${roleBadge(m.systemRole || m.role)}`}>
                        {(m.systemRole || m.role || 'member').replace(/_/g, ' ')}
                      </span>
                    </span>
                    <span className={`text-xs font-semibold ${m.isActive ? 'text-[#15803d]' : 'text-red-600'}`}>
                      {m.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <div className='flex justify-end gap-2 text-xs'>
                      <button
                        onClick={() => navigate(`/admin/users/${m.id}`)}
                        className='px-3 py-1 rounded-[8px] bg-[#151515] hover:bg-[#2a2a2a] text-white font-bold'
                      >
                        View User
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Permissions summary */}
          {members.some((m) => m.permissions && m.permissions.length > 0) && (
            <div className='mt-4'>
              <h3 className='text-[14px] font-semibold text-[#475467] mb-2'>Permissions Overview</h3>
              <div className='space-y-2'>
                {members
                  .filter((m) => m.permissions && m.permissions.length > 0)
                  .map((m) => (
                    <div key={m.id} className='flex items-start gap-3 text-xs'>
                      <span className='font-semibold text-[#101828] min-w-[120px]'>
                        {[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}
                      </span>
                      <div className='flex flex-wrap gap-1'>
                        {m.permissions?.map((p) => (
                          <span key={p} className='px-2 py-0.5 bg-[rgba(237,239,243,0.5)] text-[#475467] rounded capitalize'>
                            {p.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Couples in this organization */}
        <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4'>
          <h2 className='text-[18px] font-bold text-[#151515] mb-4'>Couples</h2>
          {couples.length === 0 ? (
            <p className='text-[14px] text-[#4e4d4d]'>No couples found</p>
          ) : (
            <div className='bg-[rgba(237,239,243,0.5)] rounded-[8px] overflow-hidden'>
              <div className='grid grid-cols-4 gap-2 px-4 py-2 font-medium text-[14px] text-[#475467]'>
                <span>Partner A</span>
                <span>Partner B</span>
                <span>Status</span>
                <span className='text-right'>Actions</span>
              </div>
              {couples.map((c) => (
                <div
                  key={c.id}
                  className='grid grid-cols-4 gap-2 px-4 py-3 text-[14px] text-[#101828] border-b border-[#eaecf0] hover:bg-[#f9fafb] bg-white items-center'
                >
                  <span className='font-semibold'>{c.partnerAName || 'Partner A'}</span>
                  <span>{c.partnerBName || 'Partner B'}</span>
                  <span className='capitalize'>{c.status || '-'}</span>
                  <div className='flex justify-end'>
                    <button
                      onClick={() => navigate(`/admin/couples/${c.id}`)}
                      className='text-xs px-3 py-1 rounded-[8px] bg-[#151515] hover:bg-[#2a2a2a] text-white font-bold'
                    >
                      View Detail
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
